/**
 * RTWO Truthful Data Guard
 * Version: 1.0 Enterprise Edition
 *
 * Enforces the RTWO Truthful Data Policy across all dashboards.
 * No fake, demo, simulated, or invented blockchain data is permitted.
 *
 * Environment Flags:
 *   RTWO_ALLOW_DEMO_DATA=false
 *   RTWO_REQUIRE_REAL_DATA=true
 *   RTWO_HIDE_PLACEHOLDERS=true
 *   RTWO_TRUTHFUL_STATUS_MODE=true
 */

// ─────────────────────────────────────────────
// POLICY FLAGS (read from env at runtime)
// ─────────────────────────────────────────────

export const RTWO_POLICY = {
  ALLOW_DEMO_DATA: process.env.RTWO_ALLOW_DEMO_DATA === 'true',
  REQUIRE_REAL_DATA: process.env.RTWO_REQUIRE_REAL_DATA !== 'false',
  HIDE_PLACEHOLDERS: process.env.RTWO_HIDE_PLACEHOLDERS !== 'false',
  TRUTHFUL_STATUS_MODE: process.env.RTWO_TRUTHFUL_STATUS_MODE !== 'false',
} as const;

// ─────────────────────────────────────────────
// PERMITTED SYSTEM STATES
// ─────────────────────────────────────────────

export type PermittedState =
  | 'Architecture Phase'
  | 'Structure Complete'
  | 'Development'
  | 'Configuration Required'
  | 'Waiting for Real Data'
  | 'Not Configured'
  | 'Not Connected'
  | 'Synchronizing'
  | 'Connected'
  | 'Operational'
  | 'Error';

// ─────────────────────────────────────────────
// DATA SOURCE LABELS
// ─────────────────────────────────────────────

export type DataSourceLabel =
  | 'RTWO Core Engine'
  | 'RTWO Blockchain Protocol'
  | 'RTWO Consensus Engine'
  | 'RTWO Ledger Engine'
  | 'RTWO RPC Node'
  | 'RTWO Database'
  | 'Real RPC'
  | 'Real Database'
  | 'Real Validator Node'
  | 'Real Indexer'
  | 'Real Wallet Service'
  | 'Not Configured'
  | 'Waiting for Real Data'
  | 'Configuration'
  | 'Real RPC — Not Connected'
  | 'Real Validator Node — Not Connected'
  | 'Real Database — Not Configured'
  | 'Real Wallet Service — Not Configured'
  | 'Real Indexer — Not Configured';

// ─────────────────────────────────────────────
// METRIC CATEGORIES
// ─────────────────────────────────────────────

export type MetricCategory =
  | 'block_height'
  | 'transactions'
  | 'validators'
  | 'wallet_balance'
  | 'staking_rewards'
  | 'rpc_health'
  | 'tps'
  | 'peer_count'
  | 'governance_votes'
  | 'node_uptime'
  | 'mainnet_status';

// ─────────────────────────────────────────────
// TRUTHFUL METRIC WRAPPER
// ─────────────────────────────────────────────

export interface TruthfulMetric<T = string> {
  value: T | null;
  source: DataSourceLabel;
  state: PermittedState;
  verified: boolean;
  lastChecked: null; // always null until real infrastructure connected
}

// ─────────────────────────────────────────────
// GUARD FUNCTIONS
// ─────────────────────────────────────────────

/**
 * Returns a truthful metric wrapper.
 * Since no real infrastructure is connected, all values are null
 * and state is "Waiting for Real Data".
 */
export function guardMetric<T = string>(
  category: MetricCategory,
  realValue?: T | null
): TruthfulMetric<T> {
  // If demo data is explicitly allowed (never in production), pass through
  if (RTWO_POLICY.ALLOW_DEMO_DATA && realValue !== undefined && realValue !== null) {
    return {
      value: realValue,
      source: 'Waiting for Real Data',
      state: 'Waiting for Real Data',
      verified: false,
      lastChecked: null,
    };
  }

  // No real infrastructure connected — return truthful empty state
  return {
    value: null,
    source: getSourceForCategory(category),
    state: 'Waiting for Real Data',
    verified: false,
    lastChecked: null,
  };
}

/**
 * Returns the expected data source label for a given metric category.
 */
export function getSourceForCategory(category: MetricCategory): DataSourceLabel {
  const sourceMap: Record<MetricCategory, DataSourceLabel> = {
    block_height: 'Real RPC',
    transactions: 'Real Indexer',
    validators: 'Real Validator Node',
    wallet_balance: 'Real Wallet Service',
    staking_rewards: 'Real Validator Node',
    rpc_health: 'Real RPC',
    tps: 'Real RPC',
    peer_count: 'Real RPC',
    governance_votes: 'Real Database',
    node_uptime: 'Real Validator Node',
    mainnet_status: 'Real RPC',
  };
  return sourceMap[category] ?? 'Not Configured';
}

/**
 * Returns the display value for a metric — always "Waiting for Real Data"
 * when no real infrastructure is connected.
 */
export function getDisplayValue(metric: TruthfulMetric): string {
  if (metric.verified && metric.value !== null) {
    return String(metric.value);
  }
  return 'Waiting for Real Data';
}

/**
 * Validates whether a system component is truly operational.
 * Returns false unless verified by real backend checks.
 */
export function isVerifiedOperational(componentId: string): boolean {
  // No real backend connected — never claim operational
  return false;
}

/**
 * Returns the truthful system state for a given component.
 */
export function getTruthfulState(componentId: string): PermittedState {
  return 'Waiting for Real Data';
}

// ─────────────────────────────────────────────
// POLICY SUMMARY (for System Integrity page)
// ─────────────────────────────────────────────

export interface PolicyStatus {
  category: string;
  description: string;
  policyEnforced: boolean;
  currentState: PermittedState;
  source: DataSourceLabel;
}

export const POLICY_STATUS_ITEMS: PolicyStatus[] = [
  {
    category: 'Demo Data',
    description: 'Demo / fake / simulated blockchain data',
    policyEnforced: !RTWO_POLICY.ALLOW_DEMO_DATA,
    currentState: 'Not Configured',
    source: 'Not Configured',
  },
  {
    category: 'Real Data Requirement',
    description: 'All metrics must originate from real infrastructure',
    policyEnforced: RTWO_POLICY.REQUIRE_REAL_DATA,
    currentState: 'Waiting for Real Data',
    source: 'Not Configured',
  },
  {
    category: 'Placeholder Hiding',
    description: 'Placeholder values are hidden from all dashboards',
    policyEnforced: RTWO_POLICY.HIDE_PLACEHOLDERS,
    currentState: 'Not Configured',
    source: 'Not Configured',
  },
  {
    category: 'Mainnet Status',
    description: 'Mainnet not live unless verified by real RPC',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real RPC',
  },
  {
    category: 'RPC Health',
    description: 'RPC not operational unless verified by real endpoint',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real RPC',
  },
  {
    category: 'Validator Data',
    description: 'Validator data not shown unless from real validator nodes',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real Validator Node',
  },
  {
    category: 'Wallet Balances',
    description: 'Wallet balances not shown unless from real wallet service',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real Wallet Service',
  },
  {
    category: 'Governance Data',
    description: 'Governance data not shown unless from real database',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real Database',
  },
  {
    category: 'Block Height',
    description: 'Block height not shown unless from real RPC',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real RPC',
  },
  {
    category: 'TPS Metrics',
    description: 'TPS not shown unless from real RPC',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real RPC',
  },
  {
    category: 'Staking Rewards',
    description: 'Staking rewards not shown unless from real validator node',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real Validator Node',
  },
  {
    category: 'Transaction Feed',
    description: 'Transactions not shown unless from real indexer',
    policyEnforced: true,
    currentState: 'Waiting for Real Data',
    source: 'Real Indexer',
  },
];

// ─────────────────────────────────────────────
// DASHBOARD SCOPE REGISTRY
// ─────────────────────────────────────────────

export const DASHBOARD_SCOPE = [
  'Core Engine Dashboard',
  'Blockchain Explorer',
  'Governance Dashboard',
  'Validator & Staking Dashboard',
  'Wallet & Assets Dashboard',
  'Enterprise Admin Panel',
  'Blockchain Protocol Engine',
  'Consensus Engine',
  'Ledger & State Engine',
  'Monitoring Dashboard',
  'Security Dashboard',
  'Developer Portal',
  'RPC Dashboard',
  'All APIs',
  'All Charts',
  'All Status Cards',
  'All Tables',
] as const;
